// Quadro de atendimentos em andamento (compartilhado por todos os usuários).
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button, Card, CardActionArea, CardContent, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Tab, Tabs, TextField, Typography, makeStyles,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { usePermissao } from "../permissoes";
import { DialogCard, Etiquetas, agoraLocal, dataBR, duracao, interno, minutosDesde, useListas } from "../comum";
import { EVENTO_CARD } from "../CardDrawer";
import { DetalheResposta } from "../Formularios";

const useStyles = makeStyles((theme) => ({
  grade: { padding: theme.spacing(1), overflowY: "auto", ...theme.scrollbarStyles },
  card: { borderLeft: `4px solid ${theme.palette.success.main}`, height: "100%" },
  demorado: { borderLeftColor: theme.palette.warning.main },
  atrasado: { borderLeftColor: theme.palette.error.main },
  topo: { display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  ultimo: {
    fontStyle: "italic", color: theme.palette.text.secondary, fontSize: 12.5,
    borderTop: `1px dashed ${theme.palette.divider}`, marginTop: 6, paddingTop: 4,
  },
  filtros: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  // formulário para validar (e atendimento com formulário pendente): roxo, para destacar no quadro
  validar: { borderLeft: "4px solid #7b1fa2", height: "100%", background: fade("#7b1fa2", theme.palette.type === "dark" ? 0.22 : 0.08) },
  roxo: { color: theme.palette.type === "dark" ? "#ce93d8" : "#7b1fa2" },
  legenda: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: theme.palette.text.secondary, margin: theme.spacing(0, 1) },
  ponto: { display: "inline-block", width: 10, height: 10, borderRadius: 2, marginRight: 4, verticalAlign: "middle" },
}));

const NovoAtendimento = ({ open, onClose, onCriado }) => {
  const listas = useListas();
  const [f, setF] = useState({});
  const [info, setInfo] = useState({});
  const [clientes, setClientes] = useState([]);
  const [modo, setModo] = useState("chamado"); // chamado técnico | interno
  const [areas, setAreas] = useState([]); // áreas de suporte que o usuário pode escolher (só aparece com mais de uma)
  const [listasArea, setListasArea] = useState(null); // tipo/sistema só da área escolhida
  useEffect(() => {
    if (!open) return;
    suporteApi.get("/api/areas").then(({ data }) => {
      const opcoes = data.areas.filter((a) => a.tipo === "suporte" && a.ativo && (a.filas.length || a.codigo === data.padrao)
        && (!data.minhas || data.minhas.includes(a.codigo)));
      setAreas(opcoes.length > 1 ? opcoes : []);
    }).catch(() => setAreas([]));
  }, [open]);
  useEffect(() => {
    if (!f.area) { setListasArea(null); return; }
    suporteApi.get("/api/listas", { params: { area: f.area } }).then(({ data }) => setListasArea(data)).catch(() => setListasArea(null));
  }, [f.area]);
  const tipos = (listasArea || listas).tipo || [];
  const sistemas = (listasArea || listas).sistema || [];
  useEffect(() => {
    if (open) {
      setModo("chamado");
      setF({ qra: "", conta: "", os: "", tipo: "", sistema: "", inicio: agoraLocal(), observacao: "",
        titulo: "", solicitante: "", iniciar_whatsapp: true, enviar_formularios: true });
      setInfo({});
      setClientes([]);
    }
  }, [open]);
  const campo = (nome) => ({
    value: f[nome] ?? "", onChange: (e) => setF({ ...f, [nome]: e.target.value }),
    fullWidth: true, size: "small", variant: "outlined", margin: "dense",
  });
  const buscarQra = async () => {
    if (f.qra === "") return;
    try {
      const { data } = await suporteApi.get(`/api/tecnicos/qra/${f.qra}`);
      setInfo((i) => ({ ...i, tecnico: `${data.nome} (${data.status})` }));
    } catch {
      setInfo((i) => ({ ...i, tecnico: "QRA não cadastrado" }));
    }
  };
  const buscarConta = async () => {
    if (!f.conta) return;
    try {
      const { data } = await suporteApi.get(`/api/clientes/conta/${encodeURIComponent(f.conta)}`);
      setClientes(data);
      setF((s) => ({ ...s, cliente_id: data[0]?.id }));
      setInfo((i) => ({ ...i, cliente: data.length === 1 ? `${data[0].nome} · ${data[0].grupo || ""}` : "" }));
    } catch {
      setClientes([]);
      setInfo((i) => ({ ...i, cliente: "Conta não cadastrada" }));
    }
  };
  const salvar = async () => {
    try {
      const { data } =
        modo === "interno"
          ? await suporteApi.post("/api/atendimentos/interno", {
              titulo: f.titulo, solicitante: f.solicitante, observacao: f.observacao,
              conta: f.conta || "", cliente_id: f.conta ? f.cliente_id || null : null,
            })
          : await suporteApi.post("/api/atendimentos", {
              ...f, qra: Number(f.qra), cliente_id: f.cliente_id || null,
            });
      toast.success(modo === "interno" ? "Chamado interno aberto"
        : f.iniciar_whatsapp ? "Atendimento aberto · iniciando a conversa com o técnico no WhatsApp…" : "Atendimento aberto");
      onCriado(data);
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Novo atendimento</DialogTitle>
      <Tabs value={modo} onChange={(e, v) => setModo(v)} indicatorColor="primary" textColor="primary" variant="fullWidth">
        <Tab value="chamado" label="Chamado técnico" />
        <Tab value="interno" label="Interno (sem QRA/O.S)" />
      </Tabs>
      <DialogContent>
        {modo === "interno" ? (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Card só para acompanhar uma demanda (ex.: resgate de imagens). Não pede QRA nem O.S e é
                concluído sem protocolo.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo options={listas.demanda || []} value={f.titulo || ""}
                onInputChange={(e, v) => setF((s) => ({ ...s, titulo: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Demanda (escolha ou digite)" required
                    size="small" variant="outlined" margin="dense" />
                )}
              />
            </Grid>
            <Grid item xs={12}><TextField label="Cliente / solicitante" required {...campo("solicitante")} /></Grid>
            <Grid item xs={4}><TextField label="Conta (opcional)" {...campo("conta")} onBlur={buscarConta} /></Grid>
            <Grid item xs={8}>
              {clientes.length > 1 ? (
                <TextField select label="Cadastro da conta" {...campo("cliente_id")}>
                  {clientes.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
                </TextField>
              ) : (
                <Typography variant="body2" style={{ marginTop: 18 }}>{info.cliente}</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField label="Solicitação / observação" required multiline rows={3} {...campo("observacao")} />
            </Grid>
          </Grid>
        ) : (
        <Grid container spacing={1}>
          {areas.length > 1 && (
            <Grid item xs={12}>
              <TextField select label="Área do suporte" {...campo("area")}>
                {areas.map((a) => <MenuItem key={a.codigo} value={a.codigo}>{a.nome}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={4}><TextField label="QRA" type="number" {...campo("qra")} onBlur={buscarQra} /></Grid>
          <Grid item xs={8}><Typography variant="body2" style={{ marginTop: 18 }}>{info.tecnico}</Typography></Grid>
          <Grid item xs={4}><TextField label="Conta" {...campo("conta")} onBlur={buscarConta} /></Grid>
          <Grid item xs={8}>
            {clientes.length > 1 ? (
              <TextField select label="Cadastro da conta" {...campo("cliente_id")}>
                {clientes.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
              </TextField>
            ) : (
              <Typography variant="body2" style={{ marginTop: 18 }}>{info.cliente}</Typography>
            )}
          </Grid>
          <Grid item xs={6}><TextField label="O.S (texto livre)" {...campo("os")} /></Grid>
          <Grid item xs={6}>
            <TextField label="Início" type="datetime-local" InputLabelProps={{ shrink: true }} {...campo("inicio")} />
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Tipo de serviço" {...campo("tipo")}>
              {tipos.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select label="Sistema" {...campo("sistema")}>
              {sistemas.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><TextField label="Observação inicial" multiline rows={2} {...campo("observacao")} /></Grid>
          <Grid item xs={12}>
            <FormControlLabel label="Iniciar conversa no WhatsApp com o técnico (mensagem inicial e conversa ligada ao card)"
              control={<Checkbox size="small" color="primary" checked={!!f.iniciar_whatsapp}
                onChange={(e) => setF({ ...f, iniciar_whatsapp: e.target.checked })} />} />
            {f.iniciar_whatsapp && (
              <FormControlLabel label="Enviar também os formulários automáticos (Formulário de Dados)"
                control={<Checkbox size="small" color="primary" checked={!!f.enviar_formularios}
                  onChange={(e) => setF({ ...f, enviar_formularios: e.target.checked })} />} />
            )}
          </Grid>
        </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={salvar}>
          {modo === "interno" ? "Abrir chamado interno" : "Abrir atendimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Quadro = () => {
  const c = useStyles();
  const podeAbrir = usePermissao("cards.abrir");
  const history = useHistory();
  const [cards, setCards] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [meus, setMeus] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [novo, setNovo] = useState(false);
  const [eu, setEu] = useState(null);
  // respostas de formulário aguardando validação (inclusive as sem card, ex.: link aberto)
  const [paraValidar, setParaValidar] = useState([]);
  // metas de prazo por tipo (Gestão): verde no prazo, laranja perto (75%), vermelho estourado
  const [metas, setMetas] = useState({});
  useEffect(() => {
    suporteApi.get("/api/gestao/metas")
      .then((r) => setMetas(Object.fromEntries(r.data.tipos.filter((t) => t.minutos).map((t) => [t.tipo, t.minutos]))))
      .catch(() => {});
  }, []);
  const [resposta, setResposta] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [{ data }, r] = await Promise.all([
        suporteApi.get("/api/atendimentos"),
        suporteApi.get("/api/formularios/respostas", { params: { status: "RESPONDIDO", por_pagina: 100 } }),
      ]);
      setCards(data);
      setParaValidar(r.data.itens);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, []);

  useEffect(() => {
    carregar();
    suporteApi.get("/api/whaticket/eu").then((r) => setEu(r.data)).catch(() => {});
    const t = setInterval(() => !document.hidden && carregar(), 15000);
    window.addEventListener(EVENTO_CARD, carregar);
    return () => {
      clearInterval(t);
      window.removeEventListener(EVENTO_CARD, carregar);
    };
  }, [carregar]);

  const f = filtro.trim().toLowerCase();
  const lista = cards.filter((a) => {
    const texto = [a.os, a.conta, a.filial, a.grupo, a.tecnico, a.qra, a.analista, a.tipo, a.sistema,
      a.titulo, a.solicitante]
      .join(" ").toLowerCase();
    return (!f || texto.includes(f)) && (!meus || a.analista === eu?.nome);
  });
  // as que já têm card no quadro aparecem no próprio card ("formulário(s) para validar")
  const respostas = paraValidar.filter((e) => !cards.some((a) => a.id === e.atendimento_id)).filter((e) =>
    !f || [e.nome_formulario, e.identificacao, e.conta, e.filial, e.tecnico, e.qra, e.os].join(" ").toLowerCase().includes(f));

  return (
    <MainContainer>
      <MainHeader>
        <Title>{`Quadro de atendimentos (${lista.length})${respostas.length ? ` · ${respostas.length} formulário(s) para validar` : ""}`}</Title>
        <MainHeaderButtonsWrapper>
          <div className={c.filtros}>
            <TextField size="small" placeholder="Filtrar O.S, conta, filial, técnico…" value={filtro}
              onChange={(e) => setFiltro(e.target.value)} />
            <FormControlLabel control={<Checkbox checked={meus} onChange={(e) => setMeus(e.target.checked)} />}
              label="Só os meus" />
            {podeAbrir && (
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setNovo(true)}>
                Novo atendimento
              </Button>
            )}
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={c.legenda}>
        <span><i className={c.ponto} style={{ background: "#7b1fa2" }} />para validar</span>
        <span><i className={c.ponto} style={{ background: "#4caf50" }} />no prazo</span>
        <span><i className={c.ponto} style={{ background: "#ff9800" }} />perto do prazo</span>
        <span><i className={c.ponto} style={{ background: "#f44336" }} />fora do prazo</span>
        <span>(metas por tipo de serviço, em Gestão)</span>
      </div>
      <Grid container spacing={2} className={c.grade}>
        {lista.length === 0 && respostas.length === 0 && (
          <Grid item xs={12}><Typography color="textSecondary">Nenhum atendimento em andamento.</Typography></Grid>
        )}
            {respostas.map((e) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`f${e.id}`}>
                <Card variant="outlined" className={c.validar}>
                  <CardActionArea onClick={() => setResposta(e.id)}>
                    <CardContent>
                      <div className={c.topo}>
                        <Typography variant="subtitle1"><b>{e.identificacao || e.filial || e.conta || (e.qra != null ? `QRA ${e.qra}` : "Resposta")}</b></Typography>
                        <Typography variant="caption" color="textSecondary">{duracao(minutosDesde(e.respondido_em))}</Typography>
                      </div>
                      <Typography variant="caption" className={c.roxo}><b>📝 FORMULÁRIO PARA VALIDAR</b></Typography>
                      <Typography variant="body2">{e.nome_formulario}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {[e.os && `O.S ${e.os}`, e.qra != null && `QRA ${e.qra} ${e.tecnico || ""}`, !e.atendimento_id && "link aberto · sem card"]
                          .filter(Boolean).join(" · ") || "—"}
                      </Typography>
                      <Typography variant="body2" className={c.roxo}><b>Clique para validar</b></Typography>
                      <Typography variant="caption" color="textSecondary">{`respondido ${dataBR(e.respondido_em)}`}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
        {lista.map((a) => {
          const min = minutosDesde(a.inicio);
          const meta = !interno(a) && metas[a.tipo];
          const classe = meta ? (min > meta ? c.atrasado : min > meta * 0.75 ? c.demorado : "")
            : min > 240 ? c.atrasado : min > 60 ? c.demorado : "";
          const ult = a.ultimo_andamento;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={a.id}>
              <Card variant="outlined" className={a.formularios_para_validar > 0 ? c.validar : `${c.card} ${classe}`}>
                <CardActionArea onClick={() => setAberto(a.id)}>
                  <CardContent>
                    <div className={c.topo}>
                      <Typography variant="subtitle1">
                        <b>{interno(a) ? a.titulo : a.os ? `O.S ${a.os}` : "O.S a informar"}</b>
                      </Typography>
                      <Typography variant="caption" color="textSecondary">{duracao(min)}</Typography>
                    </div>
                    <Typography variant="body2">
                      <b>{interno(a) ? a.solicitante : a.filial || (a.conta ? `Conta ${a.conta}` : "Local a informar")}</b>
                    </Typography>
                    {interno(a) && a.filial && <Typography variant="caption" color="textSecondary">{a.filial}</Typography>}
                    {a.conta && <Typography variant="caption" color="textSecondary">{`${a.grupo || ""} · conta ${a.conta}`}</Typography>}
                    <Etiquetas card={a} />
                    {a.formularios_para_validar > 0 && (
                      <Typography variant="body2" className={c.roxo}>
                        <b>{`📝 ${a.formularios_para_validar} formulário(s) para validar`}</b>
                      </Typography>
                    )}
                    {!interno(a) && (
                      <Typography variant="body2">
                        {a.qra !== null && a.qra !== undefined ? `QRA ${a.qra} · ${a.tecnico || "não localizado"}` : "Técnico a informar"}
                      </Typography>
                    )}
                    {interno(a) && a.observacao_inicial && (
                      <Typography variant="body2" color="textSecondary" noWrap>{a.observacao_inicial}</Typography>
                    )}
                    {ult && <div className={c.ultimo}>{`“${ult.texto}” — ${ult.analista}`}</div>}
                    <Typography variant="caption" color="textSecondary">
                      {`aberto por ${a.analista || "-"} · ${dataBR(a.inicio)}`}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                {a.whatsapp_ticket_id && (
                  <Button size="small" startIcon={<WhatsAppIcon />} style={{ margin: "0 8px 8px" }}
                    onClick={() => history.push(`/tickets/${a.whatsapp_ticket_id}`)}>
                    Abrir conversa
                  </Button>
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <DialogCard id={aberto} open={!!aberto} onClose={() => { setAberto(null); carregar(); }} onMudou={carregar} />
      <DetalheResposta id={resposta} onClose={() => { setResposta(null); carregar(); }} onMudou={carregar}
        onAbrirCard={(id) => { setResposta(null); setAberto(id); }} />
      <NovoAtendimento open={novo} onClose={() => setNovo(false)} onCriado={(a) => { carregar(); setAberto(a.id); }} />
    </MainContainer>
  );
};

export default Quadro;
